import React, { memo } from "react";
import { Handle, Position, NodeProps, useNodeConnections } from "@xyflow/react";
import { MessageSquare, Clock, Webhook, Calendar } from "lucide-react";
import {
  FlowEditorTriggerNode,
  useAgentWorkflowStore,
} from "@/store/agentsStore";
import { useAgentWorkflowTriggersStore } from "@/store/agentWorkflowTriggersStore";
import { NodeWrapper } from "./NodeWrapper";
import { NodeErrorDisplay } from "./NodeErrorDisplay";

const triggerIcons: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  MESSAGE_RECEIVED: MessageSquare,
  SCHEDULED: Clock,
  WEBHOOK: Webhook,
  CALENDAR_EVENT: Calendar,
};

export const TriggerNode = memo(
  ({ data, selected }: NodeProps<FlowEditorTriggerNode>) => {
    const nodeData = data.node;
    const workflowVersionId = data.workflowVersionId;

    const getTriggerById = useAgentWorkflowTriggersStore(
      (state) => state.getTriggerById,
    );
    const trigger = nodeData.triggerId
      ? getTriggerById(nodeData.triggerId)
      : undefined;

    // Look up errors from the workflow version store
    const workflowVersion = useAgentWorkflowStore((state) =>
      workflowVersionId
        ? state.getWorkflowVersionById(workflowVersionId)
        : undefined,
    );
    const nodeErrors = workflowVersion?.errors?.[nodeData.id];

    const Icon = triggerIcons[nodeData.triggerId || ""] || MessageSquare;
    const connections = useNodeConnections({ handleType: "source" });

    return (
      <NodeWrapper
        selected={selected}
        nodeType="trigger"
        className="px-4 py-3 min-w-[220px]"
      >
        <NodeErrorDisplay errors={nodeErrors || []} />

        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Icon className="w-4 h-4 text-amber-700" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-900">
              {trigger?.name || "Trigger"}
            </span>
          </div>
        </div>
        <Handle
          type="source"
          position={Position.Bottom}
          isConnectableStart={connections.length === 0}
          className="w-3 h-3 !bg-gray-400 !border-2 !border-white"
        />
      </NodeWrapper>
    );
  },
);
